import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Button, Callout, Flex, Text } from "@radix-ui/themes";
import iosAppBanner from "../../assets/ios-app-banner.png";
import { env } from "../../env";
import { isWebIOSPlatform } from "../utils/environment";
import { AppStoreButtons } from "./app-store-buttons.component";
import { CopyToClipboardButton } from "./copy-to-clipboard-button.component";

const PLATFORM_TEXTS = {
  ios: {
    openInAppInstruction:
      "Klicken Sie auf dieses Banner oben in Ihrem Browser um zur App zu gelangen:",
    fallbackText: "Falls Sie das Banner nicht sehen",
  },
  nonIos: {
    openInAppInstruction: "Klicken Sie auf den Button um zur App zu gelangen:",
    fallbackText: "Falls der Button nicht funktioniert",
  },
} as const;

type AppRedirectButtonsProps = {
  message?: string;
  showOpenInAppButton?: boolean;
  bookingCode?: string;
};

export const AppRedirectButtons: React.FC<AppRedirectButtonsProps> = ({
  message = "Diese Funktion ist nur in der App verfügbar. Bitte laden Sie die App herunter oder öffnen Sie diese Seite in der App.",
  showOpenInAppButton = false,
  bookingCode,
}) => {
  const detectedIOS = isWebIOSPlatform();

  const texts = detectedIOS ? PLATFORM_TEXTS.ios : PLATFORM_TEXTS.nonIos;

  const getUniversalLinkUrl = () => {
    const currentUrl = window.location.href;
    const currentUrlObject = new URL(currentUrl);
    const targetUrlObject = new URL(env.universalLinkScheme);
    targetUrlObject.pathname = currentUrlObject.pathname;
    targetUrlObject.search = currentUrlObject.search;
    targetUrlObject.hash = currentUrlObject.hash;
    return targetUrlObject.toString();
  };

  const renderOpenInAppSection = () => {
    if (!showOpenInAppButton) return null;

    const platformContent = detectedIOS
      ? {
          instructionText: texts.openInAppInstruction,
          actionElement: (
            <img
              src={iosAppBanner}
              alt="Screenshot showing how to open the app"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: "var(--radius-4)",
                boxShadow: "var(--shadow-3)",
              }}
            />
          ),
        }
      : {
          instructionText: texts.openInAppInstruction,
          actionElement: (
            <Button
              asChild
              size="3"
              style={{ minWidth: "200px", alignSelf: "center" }}
            >
              <a
                href={getUniversalLinkUrl()}
                target="_blank"
                rel="noopener noreferrer"
              >
                In App öffnen
              </a>
            </Button>
          ),
        };

    return (
      <Flex direction="column" gap="4" style={{ width: "100%" }}>
        <Text>{platformContent.instructionText}</Text>
        {platformContent.actionElement}
      </Flex>
    );
  };

  const renderBookingCodeSection = () => {
    if (!bookingCode) return null;

    return (
      <Flex direction="column" gap="4" style={{ width: "100%" }}>
        <Text>
          <strong>{texts.fallbackText}</strong>, verwenden Sie die
          Buchungsnummer in der App:
        </Text>
        <Callout.Root style={{ alignSelf: "center" }}>
          <Callout.Text>
            <Flex align="center" gap="4">
              <Text>
                <strong>{bookingCode}</strong>
              </Text>
              <CopyToClipboardButton textToCopy={bookingCode} />
            </Flex>
          </Callout.Text>
        </Callout.Root>
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="6" align="start">
      <Callout.Root color="red">
        <Callout.Icon>
          <InfoCircledIcon />
        </Callout.Icon>
        <Callout.Text>{message}</Callout.Text>
      </Callout.Root>

      {renderOpenInAppSection()}
      {renderBookingCodeSection()}
      <AppStoreButtons />
    </Flex>
  );
};
